import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Flame, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Roster-wide warmup compliance widget for the coach dashboard.
 * Reads /api/warmup/team-today and shows: team average %, each player's bar,
 * and a refresh button.  Real-time accountability layer for the staff.
 */
export default function WarmupComplianceCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/warmup/team-today");
      setData(r.data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const items = data?.items || [];
  const teamAvg = data?.team_avg ?? 0;

  return (
    <section className="rounded-xl ink-card overflow-hidden" data-testid="warmup-compliance-card">
      <div className="px-5 py-4 border-b border-[var(--ink-border)] flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Flame className="h-4 w-4 text-white/60" />
          <h2 className="font-display font-bold text-lg">Warmup compliance — today</h2>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-white/40">Team avg</div>
            <div className="font-display font-black text-2xl font-mono-num text-white" data-testid="warmup-team-avg">{teamAvg}%</div>
          </div>
          <Button variant="outline" size="sm" onClick={load} className="border-white/15 text-white bg-white/5 hover:bg-white/10" data-testid="warmup-refresh">
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="px-5 py-6 text-sm text-white/40">Loading…</div>
      ) : items.length === 0 ? (
        <div className="px-5 py-6 text-sm text-white/40">No players on the roster.</div>
      ) : (
        <ul className="divide-y divide-[var(--ink-border)]">
          {items.map((p) => {
            const color = p.percent_complete >= 100 ? "#10B981" : p.percent_complete >= 60 ? "#22D3EE" : p.percent_complete > 0 ? "#FBBF24" : "#475569";
            return (
              <li key={p.user_id} className="px-5 py-3 flex items-center gap-3" data-testid={`warmup-row-${p.user_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white truncate">{p.name}</div>
                  <div className="mt-1.5 h-1.5 rounded-full bg-white/5 overflow-hidden">
                    <div className="h-full transition-all duration-300" style={{ width: `${p.percent_complete}%`, backgroundColor: color }} />
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-mono-num text-white font-bold text-sm" style={{ color }}>{p.percent_complete}%</div>
                  <div className="text-[10px] font-mono-num text-white/40">{p.completed_count}/{p.total_blocks}</div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
